import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { EvaluationTypeLabelsEnum, queryKeys } from '@/app/utils/constants';
import { useTranslations } from 'use-intl';
import { DocumentsContextProvider } from '@/app/components/shared/EntityDocuments/DocumentsContext';
import { useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { FolderIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { deleteContractDocuments } from '@/app/services/contractsService';
import { ResourceConfig } from '@/app/components/features/evaluations/ResourceView/config';
import { SummarizationNamespaces, DocumentTypes } from '@/app/utils/constants';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbMenu,
} from '@/app/ui/components/breadcrumb';
import {
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderSidechatButton,
  Header,
  HeaderButton,
} from '@/app/ui/components/header';
import { MainScrollArea } from '@/app/ui/components/main';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import { FilesList } from '@/app/components/shared/EntityDocuments/FilesList';
import { Document } from '@/app/types/models';
import { FileUploadDialog } from '@/app/components/shared/EntityDocuments/FileUploadDialog/FileUploadDialog';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(home)/files/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractHome');
  const tFiles = useTranslations('pages.files');
  const navigate = useNavigate();
  const { contractId, projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const config = ResourceConfig[SummarizationNamespaces.CONTRACT];
  const [open, setOpen] = useState(false);
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));

  const handleRowClick = (doc: Document) => {
    navigate({
      to: '/project/$projectId/contracts/$contractId/files/$documentId',
      params: { projectId, contractId, documentId: doc.id },
    });
  };

  return (
    <DocumentsContextProvider resourceId={contractId} documentsType={config.documentType}>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu />
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                <span className="line-clamp-1">{project.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={FolderIcon} />
                {t('nav.files')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderButton aria-label={tFiles('addFiles')} onClick={() => setOpen(true)}>
          <HugeiconsIcon icon={PlusSignIcon} />
        </HeaderButton>
      </Header>
      <MainScrollArea>
        <DocumentAlerts resourceId={contractId} summaryType={SummarizationNamespaces.CONTRACT} />
        <FilesList
          resourceId={contractId}
          resourceType={DocumentTypes.CONTRACT}
          onAddClickAction={() => setOpen(true)}
          deleteDocumentsAction={deleteContractDocuments}
          detailsQueryKey={[queryKeys.CONTRACT_DETAILS, contractId]}
          showVersions
          onRowClick={handleRowClick}
        />
      </MainScrollArea>
      <FileUploadDialog
        resourceId={contractId}
        resourceType={DocumentTypes.CONTRACT}
        open={open}
        onOpenChange={setOpen}
        detailsQueryKey={[queryKeys.CONTRACT_DETAILS, contractId]}
      />
    </DocumentsContextProvider>
  );
}
